import React from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

const TARGET_FIELDS = [
  { key: "fecha", label: "Fecha", required: false, aliases: ["Fecha", "fecha", "date"] },
  { key: "concepto", label: "Concepto", required: true, aliases: ["Concepto", "concepto", "description"] },
  { key: "detalle", label: "Detalle", required: false, aliases: ["Detalle", "detalle", "detail"] },
  { key: "cantidad", label: "Cantidad", required: true, aliases: ["Cantidad", "cantidad", "importe", "amount"] },
  { key: "categoria", label: "Categoria", required: false, aliases: ["Categoria", "Categoría", "categoria", "category"] },
];

export class ImportTransactionsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sourceType: "file",
      googleSheetUrl: "",
      googleSheetUrlsText: "",
      fileName: "",
      sources: [],
      sourceAccountingMonths: {},
      rawMatrix: [],
      headerRowIndex: 0,
      columns: [],
      mapping: {
        fecha: "",
        concepto: "",
        detalle: "",
        cantidad: "",
        categoria: "",
      },
      step: "source",
      error: "",
      importWarnings: [],
      discardedRowsToInclude: new Set(),
      existingCategories: [],
      categoryMapping: {},
      detectedCategories: [],
      loadingCategories: false,
      categorySearchText: {}, // Para almacenar el text del input de cada categoría
      categoryDropdownOpen: {},
    };
  }

  resetState = () => {
    this.setState({
      sourceType: "file",
      googleSheetUrl: "",
      googleSheetUrlsText: "",
      fileName: "",
      sources: [],
      sourceAccountingMonths: {},
      rawMatrix: [],
      headerRowIndex: 0,
      columns: [],
      mapping: {
        fecha: "",
        concepto: "",
        detalle: "",
        cantidad: "",
        categoria: "",
      },
      step: "source",
      error: "",
      importWarnings: [],
      discardedRowsToInclude: new Set(),
      existingCategories: [],
      categoryMapping: {},
      detectedCategories: [],
      loadingCategories: false,
      categorySearchText: {},
      categoryDropdownOpen: {},
    });
  };

  closeModal = () => {
    this.resetState();
    this.props.onClose();
  };

  normalizeCell = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
  formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("es-ES", {
      style: "currency",
      currency: "EUR",
    });
  };

  columnLabel = (index) => `Columna ${String.fromCharCode(65 + (index % 26))}${index >= 26 ? Math.floor(index / 26) : ""}`;

  scoreHeaderRow = (row) => {
    const normalized = (row || []).map(this.normalizeCell);
    let score = 0;

    TARGET_FIELDS.forEach((field) => {
      const found = normalized.find((cell) => {
        const lower = cell.toLowerCase();
        return field.aliases.some((alias) => {
          const a = alias.toLowerCase();
          return lower === a || lower.includes(a);
        });
      });
      if (found) {
        const exact = field.aliases.some((alias) => found.toLowerCase() === alias.toLowerCase());
        score += exact ? 4 : 2;
      }
    });

    const nonEmpty = normalized.filter(Boolean).length;
    if (nonEmpty >= 3) score += 1;
    return score;
  };

  detectHeaderRow = (matrix) => {
    const candidates = (matrix || []).slice(0, 10);
    let bestIndex = 0;
    let bestScore = -1;
    candidates.forEach((row, index) => {
      const score = this.scoreHeaderRow(row);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });
    return bestIndex;
  };

  buildColumnsFromHeaderRow = (matrix, headerRowIndex) => {
    const row = matrix?.[headerRowIndex] || [];
    return row
      .map((cell, index) => ({
        key: `col_${index}`,
        index,
        label: this.normalizeCell(cell) || this.columnLabel(index),
        rawLabel: this.normalizeCell(cell),
      }))
      .filter((column) => column.rawLabel || column.index < 10);
  };

  guessMapping(columns) {
    const next = { fecha: "", concepto: "", detalle: "", cantidad: "", categoria: "" };
    TARGET_FIELDS.forEach((field) => {
      const match = columns.find((column) =>
        field.aliases.some((alias) => {
          const label = String(column.label).toLowerCase();
          const a = alias.toLowerCase();
          return label === a || label.includes(a);
        })
      );
      if (match) {
        next[field.key] = match.key;
      }
    });
    return next;
  }

  buildMatrixFromObjects = (rows) => {
    const safeRows = Array.isArray(rows) ? rows.filter((row) => row && typeof row === "object") : [];
    if (!safeRows.length) return [];
    const header = Object.keys(safeRows[0]);
    return [header, ...safeRows.map((row) => header.map((key) => row[key]))];
  };

  readFileToMatrix = async (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext === "csv") {
      const text = await file.text();
      return Papa.parse(text, { header: false, skipEmptyLines: true }).data;
    }
    if (ext === "json") {
      const text = await file.text();
      const json = JSON.parse(text);
      return this.buildMatrixFromObjects(Array.isArray(json) ? json : []);
    }
    if (ext === "xlsx" || ext === "xls") {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      return XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    }
    throw new Error(`Formato no soportado: ${file.name}`);
  };

  loadSources = (sources, sourceLabel) => {
    const safeSources = (sources || []).filter((source) => Array.isArray(source.rawMatrix) && source.rawMatrix.length > 0);
    if (!safeSources.length) {
      this.setState({ error: "No se han encontrado filas válidas en la importación." });
      return;
    }

    const primary = safeSources[0];
    const sourceAccountingMonths = {};
    safeSources.forEach((s) => {
      sourceAccountingMonths[s.name] = "";
    });
    this.setState(
      {
        sources: safeSources,
        sourceAccountingMonths,
        rawMatrix: primary.rawMatrix,
        headerRowIndex: primary.headerRowIndex,
        columns: primary.columns,
        fileName: sourceLabel,
        mapping: this.guessMapping(primary.columns),
        step: "mapping",
        error: "",
        discardedRowsToInclude: new Set(),
        categoryMapping: {},
      },
      () => {
        this.detectAndLoadCategories();
      }
    );
  };

  detectAndLoadCategories = async () => {
    const detected = this.collectDetectedCategories();
    this.setState({ detectedCategories: detected });
    await this.getExistingCategories();
  };

  createSourceFromMatrix = (matrix, sourceLabel, sourceDisplayName = null) => {
    const safeMatrix = Array.isArray(matrix)
      ? matrix.filter((row) => Array.isArray(row) && row.some((cell) => this.normalizeCell(cell)))
      : [];

    if (!safeMatrix.length) {
      return null;
    }

    const headerRowIndex = this.detectHeaderRow(safeMatrix);
    const columns = this.buildColumnsFromHeaderRow(safeMatrix, headerRowIndex);

    return {
      name: sourceLabel,
      displayName: sourceDisplayName || sourceLabel,
      rawMatrix: safeMatrix,
      headerRowIndex,
      columns,
    };
  };

  extractGoogleSheetId = (url) => {
    try {
      const parsed = new URL(url);
      const match = parsed.pathname.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      return match ? match[1] : null;
    } catch (error) {
      return null;
    }
  };

  getGoogleSheetDisplayName = async (url, index = 0) => {
    const fallback = `Fichero ${index + 1}`;
    try {
      const parsed = new URL(url);
      const candidate =
        parsed.searchParams.get("sheet") ||
        parsed.searchParams.get("name") ||
        parsed.searchParams.get("title");
      const cleaned = this.normalizeCell(candidate);
      if (cleaned) return cleaned;

      const sheetId = this.extractGoogleSheetId(url);
      if (!sheetId) return fallback;

      // Best effort: para hojas públicas puede devolver el título del fichero.
      const metadataUrl = `https://spreadsheets.google.com/feeds/worksheets/${sheetId}/public/basic?alt=json`;
      const response = await fetch(metadataUrl);
      if (response.ok) {
        const metadata = await response.json();
        const fileTitle = this.normalizeCell(metadata?.feed?.title?.$t);
        if (fileTitle) return fileTitle;
      }

      return `${fallback} (${sheetId.slice(0, 8)})`;
    } catch (error) {
      return fallback;
    }
  };

  handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      this.props.onLoading(true);
      const matrix = await this.readFileToMatrix(file);
      const source = this.createSourceFromMatrix(matrix, file.name);
      this.loadSources(source ? [source] : [], file.name);
    } catch (error) {
      console.error("Error leyendo importación:", error);
      this.setState({ error: "No se pudo leer el fichero seleccionado." });
    } finally {
      this.props.onLoading(false);
      event.target.value = "";
    }
  };

  handleFolderChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      this.props.onLoading(true);
      const supported = files.filter((file) => /\.(csv|json|xlsx|xls)$/i.test(file.name));
      const sources = [];
      for (const file of supported) {
        try {
          const matrix = await this.readFileToMatrix(file);
          const source = this.createSourceFromMatrix(matrix, file.webkitRelativePath || file.name);
          if (source) sources.push(source);
        } catch (error) {
          console.error("Error leyendo fichero de carpeta:", file.name, error);
        }
      }
      this.loadSources(sources, `Carpeta (${sources.length} fichero(s))`);
    } catch (error) {
      console.error("Error leyendo carpeta:", error);
      this.setState({ error: "No se pudo leer la carpeta seleccionada." });
    } finally {
      this.props.onLoading(false);
      event.target.value = "";
    }
  };

  toGoogleSheetCsvUrl(url, forceFirstTab = false) {
    try {
      const parsed = new URL(url);
      const match = parsed.pathname.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (!match) return null;
      const id = match[1];
      const gid = forceFirstTab ? "0" : parsed.searchParams.get("gid") || "0";
      return `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&gid=${gid}`;
    } catch (error) {
      return null;
    }
  }

  importGoogleSheet = async () => {
    const csvUrl = this.toGoogleSheetCsvUrl(this.state.googleSheetUrl);
    if (!csvUrl) {
      this.setState({ error: "La URL de Google Sheets no es válida." });
      return;
    }

    try {
      this.props.onLoading(true);
      const response = await fetch(csvUrl);
      const text = await response.text();
      const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
      const source = this.createSourceFromMatrix(parsed.data, "Google Sheets");
      this.loadSources(source ? [source] : [], "Google Sheets");
    } catch (error) {
      console.error("Error importando Google Sheets:", error);
      this.setState({ error: "No se pudo importar desde Google Sheets. La hoja debe ser accesible/publicada." });
    } finally {
      this.props.onLoading(false);
    }
  };

  importGoogleSheetsBatch = async () => {
    const urls = (this.state.googleSheetUrlsText || "")
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);

    if (!urls.length) {
      this.setState({ error: "Pega al menos una URL de Google Sheets." });
      return;
    }

    if (urls.some((url) => /drive\/folders\//.test(url))) {
      this.setState({
        error:
          "No se puede recorrer automáticamente una carpeta de Google Drive desde navegador sin API/autenticación de Drive. Pega las URLs de las Google Sheets, una por línea, o descarga la carpeta localmente e impórtala como carpeta.",
      });
      return;
    }

    try {
      this.props.onLoading(true);
      const sources = [];
      const importWarnings = [];
      for (let index = 0; index < urls.length; index++) {
        const url = urls[index];
        const csvUrl = this.toGoogleSheetCsvUrl(url, true);
        const displayName = await this.getGoogleSheetDisplayName(url, index);
        if (!csvUrl) {
          importWarnings.push(`${displayName}: URL no válida.`);
          continue;
        }
        try {
          const response = await fetch(csvUrl);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const text = await response.text();
          const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
          const source = this.createSourceFromMatrix(parsed.data, url, displayName);
          if (source) {
            sources.push(source);
          } else {
            importWarnings.push(`${displayName}: sin filas válidas en la pestaña 1.`);
          }
        } catch (error) {
          console.error("Error importando Google Sheet del lote:", url, error);
          importWarnings.push(`${displayName}: sin acceso o no publicada (pestaña 1).`);
        }
      }

      if (!sources.length) {
        this.setState({
          importWarnings,
          error: "No se pudo cargar ninguna hoja del lote. Revisa permisos/publicación de cada Google Sheet.",
        });
        return;
      }

      this.setState({ importWarnings });
      this.loadSources(sources, `Google Sheets (${sources.length} hoja(s))`);
    } catch (error) {
      console.error("Error importando lote de Google Sheets:", error);
      this.setState({ error: "No se pudo importar el lote de Google Sheets." });
    } finally {
      this.props.onLoading(false);
    }
  };

  buildMappedRows() {
    const { sources, sourceAccountingMonths, rawMatrix, headerRowIndex, mapping, columns } = this.state;
    const columnMap = Object.fromEntries(columns.map((column) => [column.key, column.index]));
    const effectiveSources = sources.length
      ? sources
      : [{ name: "source", rawMatrix, headerRowIndex }];

    return effectiveSources.flatMap((source) =>
      (source.rawMatrix || []).slice((source.headerRowIndex ?? 0) + 1).map((row) => ({
        fecha: mapping.fecha ? row[columnMap[mapping.fecha]] : "",
        concepto: mapping.concepto ? row[columnMap[mapping.concepto]] : "",
        detalle: mapping.detalle ? row[columnMap[mapping.detalle]] : "",
        cantidad: mapping.cantidad ? row[columnMap[mapping.cantidad]] : "",
        categoria: mapping.categoria ? row[columnMap[mapping.categoria]] : "",
        _sourceName: source.displayName || source.name,
        _sourceAccountingMonth: sourceAccountingMonths[source.name] || "",
      }))
    );
  }

  formatMonthKey(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }

  buildAccountingDateForRow = (row) => {
    const month = String(row?._sourceAccountingMonth || "").trim();
    if (!month) return null;

    const fecha = row?.fecha instanceof Date && !isNaN(row.fecha.getTime())
      ? row.fecha
      : null;

    if (fecha && this.formatMonthKey(fecha) === month) {
      return null;
    }

    return `${month}-01`;
  };

  parseImportDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && !isNaN(value.getTime())) return value;

    const txt = String(value).trim();
    if (!txt) return null;

    if (/^\d+(\.\d+)?$/.test(txt)) {
      const n = Number(txt);
      if (n > 20000 && n < 100000) {
        const base = new Date(Date.UTC(1899, 11, 30));
        base.setUTCDate(base.getUTCDate() + Math.floor(n));
        return base;
      }
    }

    if (txt.includes("/")) {
      const p = txt.split("/");
      if (p.length === 3) {
        const [d, m, y] = p;
        const dt = new Date(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
        if (!isNaN(dt.getTime())) return dt;
      }
    }

    const iso = new Date(txt);
    return isNaN(iso.getTime()) ? null : iso;
  };

  parseImportAmount = (value) => {
    if (value === null || value === undefined) return NaN;
    if (typeof value === "number") return value;

    let txt = String(value).trim();
    if (!txt) return NaN;

    txt = txt
      .replace(/€/g, "")
      .replace(/\s/g, "")
      .replace(/[^0-9,.-]/g, "");

    const hasComma = txt.includes(",");
    const hasDot = txt.includes(".");

    if (hasComma && hasDot) {
      txt = txt.replace(/\./g, "").replace(",", ".");
    } else if (hasComma) {
      txt = txt.replace(",", ".");
    }

    return Number(txt);
  };

  isSummaryLikeRow = (row) => {
    const haystack = [row.concepto, row.detalle, row.categoria]
      .map((x) => this.normalizeCell(x).toLowerCase())
      .join(" | ");

    const summaryTokens = [
      "total",
      "totales",
      "resumen",
      "presupuesto",
      "presupuestos",
      "gastado",
      "restante",
      "porcentaje",
      "% restante",
      "suma detalle",
      "totales i/g",
    ];

    return summaryTokens.some((token) => haystack.includes(token));
  };

  getExistingCategories = async () => {
    try {
      this.setState({ loadingCategories: true });
      const response = await doFetch("CCON", "getAllCategorias", null);
      const data = await response.json();
      if (data?.STATE === "SUCCESS" && Array.isArray(data?.DATA)) {
        const categories = data.DATA.map((cat) => ({
          IDCategoria: cat.IDCategoria,
          nombre: cat.nombre || "",
          color: cat.color || "#cccccc",
          esIngreso: cat.esIngreso === true || cat.esIngreso === 1 || cat.esIngreso === "1",
        }));
        this.setState({
          existingCategories: categories,
          loadingCategories: false,
        });
        return categories;
      } else {
        console.warn("ExistingCategories: no data or invalid STATE");
        this.setState({ loadingCategories: false });
        return [];
      }
    } catch (error) {
      console.error("Error loading existing categories:", error);
      this.setState({ loadingCategories: false, error: "No se pudieron cargar las categorías existentes." });
      return [];
    }
  };

  collectDetectedCategories = () => {
    const analysis = this.getImportAnalysis();
    let allRows = [...analysis.importRows];

    // Include categories from discarded rows that user wants to recover
    for (let i = 0; i < analysis.ignoredRows.length; i++) {
      if (this.state.discardedRowsToInclude.has(i)) {
        allRows.push(analysis.ignoredRows[i]);
      }
    }

    const uniqueCategories = [...new Set(allRows.map((r) => this.normalizeCell(r.categoria)).filter(Boolean))];
    return uniqueCategories;
  };

  findSimilarCategories = (searchTerm, existingCategories) => {
    if (!searchTerm) return [];
    const searchLower = this.normalizeCell(searchTerm).toLowerCase();
    const matches = (existingCategories || [])
      .filter((cat) => {
        const nameLower = (cat.nombre || "").toLowerCase();
        return nameLower.includes(searchLower) || searchLower.includes(nameLower);
      })
      .sort((a, b) => {
        const aLen = (a.nombre || "").length;
        const bLen = (b.nombre || "").length;
        return Math.abs(aLen - searchLower.length) - Math.abs(bLen - searchLower.length);
      });
    return matches.slice(0, 5);
  };

  getCategorySuggestions = (query, existingCategories) => {
    const q = this.normalizeCell(query).toLowerCase();
    const all = existingCategories || [];
    if (!q) {
      return all.slice(0, 8);
    }
    return all
      .filter((cat) => (cat.nombre || "").toLowerCase().includes(q))
      .slice(0, 8);
  };

  getImportAnalysis = () => {
    const mappedRows = this.buildMappedRows();
    const importRows = [];
    const ignoredRows = [];

    mappedRows.forEach((row, index) => {
      const fechaTexto = this.normalizeCell(row.fecha);
      const fecha = this.parseImportDate(row.fecha);
      const cantidad = this.parseImportAmount(row.cantidad);
      const concepto = this.normalizeCell(row.concepto);
      const detalle = this.normalizeCell(row.detalle);
      const categoria = this.normalizeCell(row.categoria);

      const reason = [];
      if (this.isSummaryLikeRow({ concepto, detalle, categoria })) {
        reason.push("resumen/total");
      }
      if (fechaTexto && !fecha) reason.push("fecha inválida");
      if (!concepto) reason.push("sin concepto");
      if (isNaN(cantidad)) reason.push("cantidad inválida");

      const normalizedRow = {
        fecha,
        fechaTexto: row.fecha,
        concepto,
        detalle,
        cantidad,
        cantidadTexto: row.cantidad,
        categoria,
        _sourceIndex: index + 1,
        _sourceName: row._sourceName,
        _sourceAccountingMonth: row._sourceAccountingMonth,
      };

      if (reason.length > 0) {
        ignoredRows.push({ ...normalizedRow, _ignoreReason: reason.join(", ") });
      } else {
        importRows.push(normalizedRow);
      }
    });

    const ingresos = importRows.filter((r) => r.cantidad > 0).reduce((acc, r) => acc + r.cantidad, 0);
    const gastos = importRows.filter((r) => r.cantidad < 0).reduce((acc, r) => acc + r.cantidad, 0);
    const balance = ingresos + gastos;

    return {
      mappedRows,
      importRows,
      ignoredRows,
      ingresos,
      gastos,
      balance,
    };
  };

  confirmImport = async () => {
    const requiredMissing = TARGET_FIELDS.filter((field) => field.required && !this.state.mapping[field.key]);
    if (requiredMissing.length > 0) {
      this.setState({ error: `Faltan campos obligatorios por mapear: ${requiredMissing.map((f) => f.label).join(", ")}` });
      return;
    }

    const analysis = this.getImportAnalysis();
    let importRows = [...analysis.importRows];

    // Include discarded rows that user selected
    for (let i = 0; i < analysis.ignoredRows.length; i++) {
      if (this.state.discardedRowsToInclude.has(i)) {
        importRows.push(analysis.ignoredRows[i]);
      }
    }

    // Apply category mapping. Si una categoría detectada no se mapea explícitamente
    // a una existente, se considera nueva por defecto.
    const categoryMap = this.state.categoryMapping || {};
    const existingCategories = this.state.existingCategories || [];
    importRows = importRows.map((row) => {
      let categoria = row.categoria;
      if (!categoria) {
        return { ...row, categoria };
      }

      const exactExisting = existingCategories.find(
        (c) => (c.nombre || "").trim().toLowerCase() === String(categoria).trim().toLowerCase()
      );
      const mapValue = categoryMap[categoria];

      if (mapValue && mapValue !== "new") {
        const existingCat = existingCategories.find(
          (c) => String(c.IDCategoria) === String(mapValue)
        );
        if (existingCat) {
          categoria = existingCat.nombre;
        }
      } else if (exactExisting) {
        categoria = exactExisting.nombre;
      } else {
        categoria = `[NEW]${categoria}`;
      }

      return { ...row, categoria };
    });

    // Crear en backend las categorías nuevas antes de importar transacciones.
    const newCategoryNames = [
      ...new Set(
        importRows
          .map((r) => r.categoria)
          .filter((c) => c && String(c).startsWith("[NEW]"))
          .map((c) => String(c).slice(5).trim())
          .filter(Boolean)
      ),
    ];

    if (newCategoryNames.length > 0) {
      try {
        const response = await doFetch("CCON", "insertCategorias", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            newCategoryNames.map((nombre) => ({ nombre, color: "#aaaaaa" }))
          ),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP ${response.status} al crear categorías: ${errorText.slice(0, 200)}`
          );
        }

        const data = await response.json();
        if (data?.STATE !== "SUCCESS") {
          this.setState({
            error: data?.MESSAGE || "No se pudieron crear las categorías nuevas.",
          });
          return;
        }
      } catch (e) {
        console.error("Error al crear categorías nuevas en la importación:", e);
        this.setState({ error: "Error al crear las categorías nuevas antes de importar." });
        return;
      }

      importRows = importRows.map((row) => ({
        ...row,
        categoria: row.categoria?.startsWith("[NEW]")
          ? row.categoria.slice(5)
          : row.categoria,
      }));
    }

    await this.props.onConfirmImport(
      importRows.map((row) => ({
        fecha: row.fecha,
        fechaImputacion: this.buildAccountingDateForRow(row),
        concepto: row.concepto,
        detalle: row.detalle,
        cantidad: row.cantidad,
        categoria: row.categoria,
      }))
    );
    this.closeModal();
  };

  goToPreview = () => {
    const requiredMissing = TARGET_FIELDS.filter((field) => field.required && !this.state.mapping[field.key]);
    if (requiredMissing.length > 0) {
      this.setState({ error: `Faltan campos obligatorios por mapear: ${requiredMissing.map((f) => f.label).join(", ")}` });
      return;
    }
    this.setState({ step: "discarded", error: "" });
  };

  goToDiscardedRows = () => {
    this.setState({ step: "discarded", error: "" });
  };

  goToCategoryMapping = () => {
    const detected = this.collectDetectedCategories();

    // Smart precload: suggest best match for each category
    const newMapping = {};
    detected.forEach((category) => {
      const similar = this.findSimilarCategories(category, this.state.existingCategories);
      if (similar.length > 0) {
        newMapping[category] = similar[0].IDCategoria;
      }
    });

    this.setState({
      step: "category-mapping",
      detectedCategories: detected,
      categoryMapping: newMapping,
      categorySearchText: {},
      categoryDropdownOpen: {},
      error: "",
    });
  };

  goToFinalPreview = () => {
    this.setState({ step: "preview", error: "" });
  };

  renderSourceStep() {
    return (
      <>
        <div className="d-flex gap-2 mb-3">
          <MDBBtn
            type="button"
            color={this.state.sourceType === "file" ? "primary" : "light"}
            onClick={() => this.setState({ sourceType: "file", error: "", importWarnings: [] })}
          >
            Archivo
          </MDBBtn>
          <MDBBtn
            type="button"
            color={this.state.sourceType === "folder" ? "primary" : "light"}
            onClick={() => this.setState({ sourceType: "folder", error: "", importWarnings: [] })}
          >
            Carpeta
          </MDBBtn>
          <MDBBtn
            type="button"
            color={this.state.sourceType === "google-sheet" ? "primary" : "light"}
            onClick={() => this.setState({ sourceType: "google-sheet", error: "", importWarnings: [] })}
          >
            Google Sheets
          </MDBBtn>
          <MDBBtn
            type="button"
            color={this.state.sourceType === "google-sheets-batch" ? "primary" : "light"}
            onClick={() => this.setState({ sourceType: "google-sheets-batch", error: "", importWarnings: [] })}
          >
            Lote Sheets
          </MDBBtn>
        </div>

        {this.state.sourceType === "file" ? (
          <div className="border rounded p-3 bg-light-subtle">
            <div className="fw-semibold mb-2">Importar desde archivo</div>
            <div className="text-muted small mb-3">
              Formatos soportados: CSV, JSON y Excel (.xlsx/.xls). Si vienes de Google Sheets, también puedes descargar la hoja como CSV o Excel e importarla aquí.
            </div>
            <input
              type="file"
              accept=".csv,.json,.xlsx,.xls,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={this.handleFileChange}
            />
          </div>
        ) : this.state.sourceType === "folder" ? (
          <div className="border rounded p-3 bg-light-subtle">
            <div className="fw-semibold mb-2">Importar carpeta de archivos</div>
            <div className="text-muted small mb-3">
              Selecciona una carpeta local con múltiples CSV, JSON o Excel. El sistema recorrerá todos los ficheros soportados.
            </div>
            <input
              type="file"
              multiple
              webkitdirectory="true"
              directory=""
              onChange={this.handleFolderChange}
            />
          </div>
        ) : this.state.sourceType === "google-sheets-batch" ? (
          <div className="border rounded p-3 bg-light-subtle">
            <div className="fw-semibold mb-2">Importar lote de Google Sheets</div>
            <div className="text-muted small mb-3">
              Pega una URL de Google Sheets por línea. Si tienes una carpeta de Drive, desde navegador no se puede recorrer automáticamente sin API/autenticación; en ese caso pega aquí las URLs de las hojas o descarga la carpeta localmente.
            </div>
            <textarea
              className="form-control"
              rows={6}
              value={this.state.googleSheetUrlsText}
              onChange={(e) => this.setState({ googleSheetUrlsText: e.target.value, error: "", importWarnings: [] })}
              placeholder="https://docs.google.com/spreadsheets/d/...\nhttps://docs.google.com/spreadsheets/d/..."
            />
            <div className="mt-3">
              <MDBBtn type="button" onClick={this.importGoogleSheetsBatch}>
                Cargar hojas
              </MDBBtn>
            </div>
          </div>
        ) : (
          <div className="border rounded p-3 bg-light-subtle">
            <div className="fw-semibold mb-2">Importar desde Google Sheets</div>
            <div className="text-muted small mb-3">
              Pega la URL de la hoja. Debe ser accesible o estar publicada para poder leerla desde el navegador.
            </div>
            <MDBInput
              label="URL de Google Sheets"
              value={this.state.googleSheetUrl}
              onChange={(e) => this.setState({ googleSheetUrl: e.target.value, error: "" })}
            />
            <div className="mt-3">
              <MDBBtn type="button" onClick={this.importGoogleSheet}>
                Cargar hoja
              </MDBBtn>
            </div>
          </div>
        )}
      </>
    );
  }

  renderPreviewStep() {
    const analysis = this.getImportAnalysis();
    let previewRows = [...analysis.importRows];
    const { sources, sourceAccountingMonths } = this.state;

    // Include recovered discarded rows
    for (let i = 0; i < analysis.ignoredRows.length; i++) {
      if (this.state.discardedRowsToInclude.has(i)) {
        previewRows.push(analysis.ignoredRows[i]);
      }
    }

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="fw-semibold">Previsualización final</div>
            <div className="text-muted small">Se van a importar {previewRows.length} fila(s) (incluidas {this.state.discardedRowsToInclude.size} descartadas recuperadas). Ignoradas: {analysis.ignoredRows.length - this.state.discardedRowsToInclude.size}.</div>
          </div>
          <MDBBtn type="button" color="light" onClick={() => this.setState({ step: "mapping", error: "" })}>
            Volver al mapeo
          </MDBBtn>
        </div>

        {sources.length > 0 && (
          <div className="border rounded p-2 mb-3" style={{ background: "#f8f9fa" }}>
            <div className="fw-semibold mb-2">Mes contable por fichero</div>
            <div className="small text-muted mb-2">
              Elige aquí el mes contable a aplicar antes de insertar en BBDD. Solo se asignará a las filas cuya fecha quede fuera del mes elegido, o a filas sin fecha.
            </div>
            <div className="d-flex flex-column gap-2">
              {sources.map((source) => (
                <div key={source.name} className="d-flex align-items-center gap-2">
                  <div className="small text-muted" style={{ minWidth: 260, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={source.displayName || source.name}>
                    {source.displayName || source.name}
                  </div>
                  <input
                    type="month"
                    className="form-control form-control-sm"
                    style={{ width: "auto" }}
                    value={sourceAccountingMonths[source.name] || ""}
                    onChange={(e) =>
                      this.setState({
                        sourceAccountingMonths: {
                          ...sourceAccountingMonths,
                          [source.name]: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="row g-2 mb-3">
          <div className="col-md-3"><div className="border rounded p-2 h-100"><div className="small text-muted">Transacciones a cargar</div><div className="fw-semibold">{previewRows.length}</div></div></div>
          <div className="col-md-3"><div className="border rounded p-2 h-100"><div className="small text-muted">Ingresos</div><div className="fw-semibold text-success">{this.formatCurrency(previewRows.filter((r) => r.cantidad > 0).reduce((acc, r) => acc + r.cantidad, 0))}</div></div></div>
          <div className="col-md-3"><div className="border rounded p-2 h-100"><div className="small text-muted">Gastos</div><div className="fw-semibold text-danger">{this.formatCurrency(previewRows.filter((r) => r.cantidad < 0).reduce((acc, r) => acc + r.cantidad, 0))}</div></div></div>
          <div className="col-md-3"><div className="border rounded p-2 h-100"><div className="small text-muted">Balance</div><div className={`fw-semibold ${(previewRows.reduce((acc, r) => acc + r.cantidad, 0)) >= 0 ? "text-success" : "text-danger"}`}>{this.formatCurrency(previewRows.reduce((acc, r) => acc + r.cantidad, 0))}</div></div></div>
        </div>

        {analysis.ignoredRows.length - this.state.discardedRowsToInclude.size > 0 && (
          <div className="alert alert-info py-2 small">
            Se han descartado automáticamente {analysis.ignoredRows.length - this.state.discardedRowsToInclude.size} fila(s) por ser totales/resúmenes o por no tener concepto, cantidad o una fecha válida cuando venía informada.
          </div>
        )}

        <div style={{ maxHeight: "55vh", overflow: "auto", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 6 }}>
          <MDBTable small striped responsive className="mb-0">
            <MDBTableHead>
              <tr>
                <th>#</th>
                <th>Origen</th>
                <th>Fecha</th>
                <th>Fecha contable</th>
                <th>Concepto</th>
                <th>Detalle</th>
                <th>Cantidad</th>
                <th>Categoría</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {previewRows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{String(row._sourceName || "")}</td>
                  <td>{row.fecha instanceof Date && !isNaN(row.fecha.getTime()) ? row.fecha.toLocaleDateString("es-ES") : String(row.fechaTexto ?? "")}</td>
                  <td>{this.buildAccountingDateForRow(row) || "-"}</td>
                  <td>{String(row.concepto ?? "")}</td>
                  <td>{String(row.detalle ?? "")}</td>
                  <td className={row.cantidad >= 0 ? "text-success" : "text-danger"}>{this.formatCurrency(row.cantidad)}</td>
                  <td>{String(row.categoria ?? "")}</td>
                </tr>
              ))}
            </MDBTableBody>
          </MDBTable>
        </div>
      </>
    );
  }

  renderMappingStep() {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="fw-semibold">Mapeo de columnas</div>
            <div className="text-muted small">Origen: {this.state.fileName} · Filas detectadas: {Math.max(0, this.state.rawMatrix.length - this.state.headerRowIndex - 1)}</div>
          </div>
          <MDBBtn type="button" color="light" onClick={() => this.setState({ step: "source", error: "" })}>
            Volver
          </MDBBtn>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <MDBInput
              type="number"
              min="1"
              label="Fila de cabecera"
              value={this.state.headerRowIndex + 1}
              onChange={(e) => {
                const nextIndex = Math.max(0, Number(e.target.value || 1) - 1);
                const columns = this.buildColumnsFromHeaderRow(this.state.rawMatrix, nextIndex);
                this.setState({
                  headerRowIndex: nextIndex,
                  columns,
                  mapping: this.guessMapping(columns),
                  error: "",
                });
              }}
            />
          </div>
          <div className="col-md-8 d-flex align-items-end">
            <div className="text-muted small">
              Detección inteligente aplicada. Si el sistema no ha acertado, ajusta la fila de cabecera manualmente.
            </div>
          </div>
        </div>

        <MDBTable small striped responsive className="mb-0">
          <MDBTableHead>
            <tr>
              <th>Campo destino</th>
              <th>Columna origen</th>
              <th>Obligatorio</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {TARGET_FIELDS.map((field) => (
              <tr key={field.key}>
                <td>{field.label}</td>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={this.state.mapping[field.key]}
                    onChange={(e) =>
                      this.setState({
                        mapping: { ...this.state.mapping, [field.key]: e.target.value },
                        error: "",
                      })
                    }
                  >
                    <option value="">No importar</option>
                    {this.state.columns.map((column) => (
                      <option key={column.key} value={column.key}>
                        {column.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{field.required ? "Sí" : "No"}</td>
              </tr>
            ))}
          </MDBTableBody>
        </MDBTable>
      </>
    );
  }

  renderDiscardedRowsStep() {
    const analysis = this.getImportAnalysis();
    const discarded = analysis.ignoredRows;

    const renderContent = () => {
      if (discarded.length === 0) {
        return (
          <div className="alert alert-success">
            No hay filas descartadas. Todas las filas de los datos son válidas.
          </div>
        );
      }

      return (
        <>
          <div className="mb-3">
            <div className="fw-semibold">Filas descartadas automáticamente</div>
            <div className="text-muted small">
              Se han descartado {discarded.length} fila(s). Revísalas a continuación y selecciona cuáles quieres incluir en la importación.
            </div>
          </div>

          <div style={{ maxHeight: "55vh", overflow: "auto", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 6 }}>
            <MDBTable small striped responsive className="mb-0">
              <MDBTableHead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        const newSet = new Set(this.state.discardedRowsToInclude);
                        if (e.target.checked) {
                          discarded.forEach((_, i) => newSet.add(i));
                        } else {
                          newSet.clear();
                        }
                        this.setState({ discardedRowsToInclude: newSet });
                      }}
                      checked={this.state.discardedRowsToInclude.size === discarded.length && discarded.length > 0}
                    />
                  </th>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Detalle</th>
                  <th>Cantidad</th>
                  <th>Categoría</th>
                  <th>Razón</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {discarded.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={this.state.discardedRowsToInclude.has(index)}
                        onChange={(e) => {
                          const newSet = new Set(this.state.discardedRowsToInclude);
                          if (e.target.checked) {
                            newSet.add(index);
                          } else {
                            newSet.delete(index);
                          }
                          this.setState({ discardedRowsToInclude: newSet });
                        }}
                      />
                    </td>
                    <td>{row._sourceIndex}</td>
                    <td>{row.fecha instanceof Date && !isNaN(row.fecha.getTime()) ? row.fecha.toLocaleDateString("es-ES") : String(row.fechaTexto ?? "")}</td>
                    <td>{String(row.concepto ?? "")}</td>
                    <td>{String(row.detalle ?? "")}</td>
                    <td className={row.cantidad >= 0 ? "text-success" : "text-danger"}>{Number(row.cantidad || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</td>
                    <td>{String(row.categoria ?? "")}</td>
                    <td>
                      <small className="text-muted">{row._ignoreReason}</small>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>
        </>
      );
    };

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="fw-semibold">Revisión de filas descartadas</div>
            <div className="text-muted small">Tienes {this.state.discardedRowsToInclude.size} / {discarded.length} filas seleccionadas para recuperar</div>
          </div>
          <MDBBtn type="button" color="light" onClick={() => this.setState({ step: "mapping", error: "" })}>
            Volver al mapeo
          </MDBBtn>
        </div>

        {renderContent()}
      </>
    );
  }

  renderCategoryMappingStep() {
    const { detectedCategories, existingCategories, categoryMapping, categorySearchText } = this.state;
    const categoriesToMap = detectedCategories.filter(Boolean);

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="fw-semibold">Mapeo de categorías</div>
            <div className="text-muted small">
              Se han detectado {categoriesToMap.length} categoría(s) ({existingCategories.length} existentes en tu cuenta). Se sugieren las más parecidas. Mapea cada una a una categoría existente o crea una nueva.
            </div>
          </div>
          <MDBBtn type="button" color="light" onClick={this.goToDiscardedRows}>
            Volver a descartadas
          </MDBBtn>
        </div>

        {categoriesToMap.length === 0 ? (
          <div className="alert alert-info">No hay categorías detectadas en los datos. Procede a la previsualización.</div>
        ) : existingCategories.length === 0 ? (
          <div className="alert alert-warning">No se han podido cargar las categorías existentes. Todas se crearán como nuevas.</div>
        ) : (
          <div style={{ maxHeight: "60vh", overflow: "auto" }}>
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>Categoría detectada</th>
                  <th>Seleccionar o crear</th>
                  <th>Color</th>
                </tr>
              </thead>
              <tbody>
                {categoriesToMap.map((category) => {
                  const currentMapValue = categoryMapping[category] || "";
                  const selectedCat = existingCategories.find((c) => String(c.IDCategoria) === String(currentMapValue));
                  const suggestions = this.findSimilarCategories(category, existingCategories);
                  const displayText = categorySearchText[category] || (selectedCat ? selectedCat.nombre : "");
                  const predictionList = this.getCategorySuggestions(displayText, existingCategories);
                  const isOpen = !!(this.state.categoryDropdownOpen || {})[category];

                  return (
                    <tr key={category}>
                      <td className="fw-semibold" style={{ minWidth: "150px" }}>
                        {category}
                      </td>
                      <td style={{ minWidth: "350px" }}>
                        <div style={{ position: "relative" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Buscar entre categorías..."
                            value={displayText}
                            onFocus={() => {
                              this.setState({
                                categoryDropdownOpen: {
                                  ...(this.state.categoryDropdownOpen || {}),
                                  [category]: true,
                                },
                              });
                            }}
                            onClick={() => {
                              this.setState({
                                categoryDropdownOpen: {
                                  ...(this.state.categoryDropdownOpen || {}),
                                  [category]: true,
                                },
                              });
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                this.setState({
                                  categoryDropdownOpen: {
                                    ...(this.state.categoryDropdownOpen || {}),
                                    [category]: false,
                                  },
                                });
                              }, 120);
                            }}
                            onChange={(e) => {
                              const val = e.target.value.trim();
                              this.setState({
                                categorySearchText: {
                                  ...categorySearchText,
                                  [category]: val,
                                },
                                categoryDropdownOpen: {
                                  ...(this.state.categoryDropdownOpen || {}),
                                  [category]: true,
                                },
                              });

                              if (!val) {
                                this.setState({
                                  categoryMapping: {
                                    ...categoryMapping,
                                    [category]: "",
                                  },
                                });
                                return;
                              }

                              const exact = existingCategories.find(
                                (c) => c.nombre.toLowerCase() === val.toLowerCase()
                              );
                              if (exact) {
                                this.setState({
                                  categoryMapping: {
                                    ...categoryMapping,
                                    [category]: exact.IDCategoria,
                                  },
                                });
                              }
                            }}
                          />
                          {isOpen && (
                            <div
                              className="border rounded bg-white shadow-sm"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                zIndex: 30,
                                maxHeight: 220,
                                overflowY: "auto",
                              }}
                            >
                              {predictionList.map((cat) => (
                                <button
                                  key={`opt-${category}-${cat.IDCategoria}`}
                                  type="button"
                                  className="btn btn-sm text-start w-100 rounded-0 border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    this.setState({
                                      categoryMapping: {
                                        ...categoryMapping,
                                        [category]: cat.IDCategoria,
                                      },
                                      categorySearchText: {
                                        ...categorySearchText,
                                        [category]: cat.nombre,
                                      },
                                      categoryDropdownOpen: {
                                        ...(this.state.categoryDropdownOpen || {}),
                                        [category]: false,
                                      },
                                    });
                                  }}
                                >
                                  {cat.nombre}
                                </button>
                              ))}
                              {!predictionList.length && !!displayText && (
                                <div className="small text-muted px-2 py-2">
                                  Sin coincidencias exactas. Se creará nueva categoría si mantienes este texto.
                                </div>
                              )}
                              {!predictionList.length && !displayText && (
                                <div className="small text-muted px-2 py-2">No hay categorías para mostrar.</div>
                              )}
                            </div>
                          )}
                        </div>
                        {suggestions.length > 0 && !selectedCat && (
                          <small className="text-muted d-block mt-1">
                            💡 Sugerencias: {suggestions.slice(0, 3).map((s) => s.nombre).join(" • ")}
                          </small>
                        )}
                        {selectedCat && (
                          <small className="text-success d-block mt-1">
                            ✓ Reutilizando: <strong>{selectedCat.nombre}</strong>
                            {currentMapValue && (
                              <button
                                className="btn btn-link btn-sm p-0 ms-2"
                                type="button"
                                onClick={() => {
                                  this.setState({
                                    categoryMapping: {
                                      ...categoryMapping,
                                      [category]: "",
                                    },
                                    categorySearchText: {
                                      ...categorySearchText,
                                      [category]: "",
                                    },
                                  });
                                }}
                              >
                                (cambiar)
                              </button>
                            )}
                          </small>
                        )}
                        {!selectedCat && displayText && (
                          <small className="text-warning d-block mt-1">
                            → Se creará nueva categoría: "{displayText}"
                          </small>
                        )}
                      </td>
                      <td style={{ minWidth: "60px" }}>
                        {selectedCat ? (
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              backgroundColor: selectedCat.color || "#cccccc",
                              borderRadius: 4,
                            }}
                            title={selectedCat.color}
                          />
                        ) : (
                          <small className="text-muted">✨ Nuevo</small>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  render() {
    return (
      <MDBModal show={this.props.show} onHide={this.closeModal} tabIndex="-1">
        <MDBModalDialog size="xl" centered style={{ maxWidth: "1400px" }}>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon icon="file-import" className="me-2" />
                Importar transacciones
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.closeModal} />
            </MDBModalHeader>
            <MDBModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
              {this.state.error && (
                <div className="alert alert-warning py-2">{this.state.error}</div>
              )}
              {this.state.importWarnings?.length > 0 && (
                <div className="alert alert-info py-2">
                  <div className="fw-semibold">Avisos de importación</div>
                  <ul className="mb-0 mt-1">
                    {this.state.importWarnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              {this.state.step === "source" && this.renderSourceStep()}
              {this.state.step === "mapping" && this.renderMappingStep()}
              {this.state.step === "discarded" && this.renderDiscardedRowsStep()}
              {this.state.step === "category-mapping" && this.renderCategoryMappingStep()}
              {this.state.step === "preview" && this.renderPreviewStep()}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn type="button" color="light" onClick={this.closeModal}>
                Cancelar
              </MDBBtn>
              {this.state.step === "mapping" && (
                <MDBBtn type="button" onClick={this.goToPreview}>
                  Siguiente (Filas descartadas)
                </MDBBtn>
              )}
              {this.state.step === "discarded" && (
                <MDBBtn type="button" onClick={this.goToCategoryMapping}>
                  Siguiente (Mapeo de categorías)
                </MDBBtn>
              )}
              {this.state.step === "category-mapping" && (
                <MDBBtn type="button" onClick={this.goToFinalPreview}>
                  Siguiente (Previsualización final)
                </MDBBtn>
              )}
              {this.state.step === "preview" && (
                <MDBBtn type="button" onClick={this.confirmImport}>
                  Importar a BBDD
                </MDBBtn>
              )}
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default ImportTransactionsModal;
